/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The calendar a programme's workout dates live on: its gym's, or this browser's when
 * the programme has no gym (no box read then). The one seam where those dates are written.
 */
import { useCallback } from "react";
import { useBoxSettings } from "./useBoxSettings";
import { useGymZone, type GymZone } from "./useGymZone";
import { useGymTodayKey } from "./useGymTodayKey";
import { gymDayKey, gymKeyMidnight } from "../lib/gymClock";

interface ProgrammeCalendarBase {
  gym: GymZone;
  keyOf: (date: Date | number | null | undefined) => string | null;
  /** The instant a workout on this day is stored at; throws until ready. */
  dateOf: (key: string) => Date;
}

export type ProgrammeCalendar = ProgrammeCalendarBase &
  (
    | { ready: true; zone: string; todayKey: string }
    /** While the gym's zone loads. */
    | { ready: false; zone: string | null; todayKey: string | null }
  );

export function useProgrammeCalendar(boxID: string | null | undefined): ProgrammeCalendar {
  const gym = useGymZone(useBoxSettings(boxID || null).zone);
  const zone = gym.zone;
  const todayKey = useGymTodayKey(zone);
  const keyOf = useCallback(
    (date: Date | number | null | undefined) => gymDayKey(date, zone),
    [zone]
  );
  const dateOf = useCallback(
    (key: string) => {
      if (!zone) throw new Error("useProgrammeCalendar: the gym's calendar has not loaded");
      return gymKeyMidnight(key, zone);
    },
    [zone]
  );
  return zone && todayKey
    ? { gym, zone, todayKey, ready: true, keyOf, dateOf }
    : { gym, zone, todayKey, ready: false, keyOf, dateOf };
}
